import React, { useMemo, useState } from 'react';
import { useTheme } from 'styled-components';
import {
  StyledContactUsContainer,
  StyledContactInfoContainer,
  StyledBottomContainer,
  StyledForm,
  StyledLabel,
  StyledInput,
  StyledTextareaContainer,
  StyledTextarea,
  StyledButtonContents,
  StyledFieldsContainer
} from './ContactUsForm.styled';
import { Button, Text } from '../../../mo-gel';
import { ReactComponent as RightArrow } from '../../../images/icons/right_icon.svg';
import { postJson } from '../../../services/api';

const CONTACT_US_FORM_API =
  'https://bhou6g6qig.execute-api.ap-southeast-2.amazonaws.com/Development/contact-us-submission';

interface ContactUsField {
  value: string;
  error: boolean;
  isRequired?: boolean;
}

type ContactUsFieldName = 'firstname' | 'lastname' | 'email' | 'phone' | 'comments';

interface ContactUsFields {
  firstname: ContactUsField;
  lastname: ContactUsField;
  email: ContactUsField;
  phone: ContactUsField;
  comments: ContactUsField;
}

interface ContactUsState {
  fields: ContactUsFields;
  loading: boolean;
  formError: boolean;
}

interface ContactUsFormProps {
  isSubmitted?: boolean;
  onNavigateBack?: (isSubmitted: boolean) => void;
}

const initialState: ContactUsState = {
  fields: {
    firstname: { value: '', error: false, isRequired: true },
    lastname: { value: '', error: false, isRequired: true },
    email: { value: '', error: false, isRequired: true },
    phone: { value: '', error: false },
    comments: { value: '', error: false, isRequired: true }
  },
  loading: false,
  formError: false
};

const ContactUsForm = ({ isSubmitted, onNavigateBack }: ContactUsFormProps) => {
  const [state, setState] = useState<ContactUsState>(initialState);
  const theme = useTheme();

  const validateField = (value: string, isRequired?: boolean) => {
    return !!(isRequired && value.trim() === '');
  };

  const updateField = (name: ContactUsFieldName, value: string) => {
    setState((current) => ({
      ...current,
      fields: {
        ...current.fields,
        [name]: {
          ...current.fields[name],
          value,
          error: validateField(value, current.fields[name].isRequired)
        }
      }
    }));
  };

  const handleBlur = (event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const name = event.target.name as ContactUsFieldName;
    const value = event.target.value;
    setState((current) => ({
      ...current,
      fields: {
        ...current.fields,
        [name]: {
          ...current.fields[name],
          error: validateField(value, current.fields[name].isRequired)
        }
      }
    }));
  };

  const formData = useMemo(
    () => ({
      firstname: state.fields.firstname.value,
      lastname: state.fields.lastname.value,
      email: state.fields.email.value,
      phone: state.fields.phone.value,
      comments: state.fields.comments.value,
      locale: 'CA'
    }),
    [
      state.fields.firstname.value,
      state.fields.lastname.value,
      state.fields.email.value,
      state.fields.phone.value,
      state.fields.comments.value
    ]
  );

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const updatedFields = (Object.keys(state.fields) as ContactUsFieldName[]).reduce(
      (acc, fieldName) => {
        const field = state.fields[fieldName];
        acc[fieldName] = {
          ...field,
          error: validateField(field.value, field.isRequired)
        };
        return acc;
      },
      {} as ContactUsFields
    );

    setState((current) => ({
      ...current,
      fields: updatedFields,
      loading: true,
      formError: false
    }));

    const requiredFields: ContactUsFieldName[] = ['firstname', 'lastname', 'email', 'comments'];
    const hasErrors = requiredFields.some((name) => updatedFields[name].error);

    if (hasErrors) {
      setState((current) => ({ ...current, fields: updatedFields, loading: false, formError: true }));
      return;
    }

    try {
      await postJson<typeof formData, unknown>(CONTACT_US_FORM_API, formData);
      setState(initialState);
      onNavigateBack?.(true);
    } catch (err) {
      console.error('Contact us failed', err);
      onNavigateBack?.(false);
      setState((current) => ({ ...current, formError: true }));
    } finally {
      setState((current) => ({ ...current, loading: false }));
    }
  };

  if (isSubmitted) {
    return null;
  }

  return (
    <StyledContactUsContainer>
      <StyledForm onSubmit={handleSubmit}>
        <StyledContactInfoContainer>
          <Text as="h6" color="black">
            Your Details:
          </Text>
          <StyledFieldsContainer>
            <StyledLabel htmlFor="firstname" error={state.fields.firstname.error}>
              First name*
            </StyledLabel>
            <StyledInput
              name="firstname"
              value={state.fields.firstname.value}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => updateField('firstname', event.target.value)}
              onBlur={handleBlur}
              error={state.fields.firstname.error}
            />
          </StyledFieldsContainer>
          <StyledFieldsContainer>
            <StyledLabel htmlFor="lastname" error={state.fields.lastname.error}>
              Last name*
            </StyledLabel>
            <StyledInput
              name="lastname"
              value={state.fields.lastname.value}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => updateField('lastname', event.target.value)}
              onBlur={handleBlur}
              error={state.fields.lastname.error}
            />
          </StyledFieldsContainer>
          <StyledFieldsContainer>
            <StyledLabel htmlFor="email" error={state.fields.email.error}>
              Email address*
            </StyledLabel>
            <StyledInput
              name="email"
              value={state.fields.email.value}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => updateField('email', event.target.value)}
              onBlur={handleBlur}
              error={state.fields.email.error}
            />
          </StyledFieldsContainer>
          <StyledFieldsContainer>
            <StyledLabel htmlFor="phone">Phone number</StyledLabel>
            <StyledInput
              name="phone"
              value={state.fields.phone.value}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => updateField('phone', event.target.value)}
            />
          </StyledFieldsContainer>
          <StyledFieldsContainer align="flex-end">
            <Text as="span" color={state.formError ? theme.colors.error : 'black'}>
              *Required field
            </Text>
          </StyledFieldsContainer>
        </StyledContactInfoContainer>
        <StyledBottomContainer>
          <StyledLabel style={{ width: '100%' }} htmlFor="comments" error={state.fields.comments.error}>
            Message*
          </StyledLabel>
          <StyledTextareaContainer error={state.fields.comments.error}>
            <StyledTextarea
              placeholder="Write your message or area of interest here"
              name="comments"
              value={state.fields.comments.value}
              onChange={(event: React.ChangeEvent<HTMLTextAreaElement>) => updateField('comments', event.target.value)}
              onBlur={handleBlur}
            />
          </StyledTextareaContainer>
          <Button type="submit" primary color={theme.colors.primaryBrand1} disabled={state.loading}>
            <StyledButtonContents>
              Send
              <RightArrow />
            </StyledButtonContents>
          </Button>
        </StyledBottomContainer>
      </StyledForm>
    </StyledContactUsContainer>
  );
};

export { ContactUsForm };

